import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import toast, { Toaster } from 'react-hot-toast';
import { fetchWalletPositions } from '../walletApi';
import { fetchPositionHistory, computeIL, computePnL } from '../graphApi';
import TokenStack from './TokenStack';
import { CHAIN_COLORS } from '../data';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtUsd(value) {
  if (value == null || isNaN(value)) return '$0.00';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtTokenAmount(amount) {
  if (amount == null || isNaN(amount)) return '0';
  if (Math.abs(amount) < 0.001) return amount.toFixed(6);
  if (Math.abs(amount) < 1) return amount.toFixed(4);
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPercent(value) {
  if (value == null || isNaN(value)) return '0.00%';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function fmtFeeTier(feeTier) {
  const pct = feeTier / 10000;
  return `${pct.toFixed(2)}%`;
}

function isValidAddress(addr) {
  return /^0x[0-9a-fA-F]{40}$/.test(addr);
}

const CHAINS = ['Ethereum', 'Arbitrum', 'Optimism', 'Base', 'Polygon'];
const CHAIN_SLUG_MAP = { Ethereum: 'ethereum', Arbitrum: 'arbitrum', Optimism: 'optimism', Base: 'base', Polygon: 'polygon' };
const EXAMPLE_WALLET = '0x11E4857Bb9993a50c685A79AFad4E6F65D518DDa';

const PIE_COLORS = ['#0D7A4E', '#5470D4', '#E8471A', '#E0006E', '#1A7A56', '#8A6A00', '#6B35C8', '#1A8ED4', '#D40010', '#0040D4'];

// Small inline SVG icons for each chain (14x14)
const CHAIN_ICONS = {
  Ethereum: (color) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, flexShrink: 0 }}>
      <polygon points="7,1 12,7 7,13 2,7" fill={color} opacity="0.9" />
      <polygon points="7,3 10,7 7,11 4,7" fill="none" stroke="#FFFFFF" strokeWidth="0.6" opacity="0.5" />
    </svg>
  ),
  Arbitrum: (color) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, flexShrink: 0 }}>
      <circle cx="7" cy="7" r="6" fill={color} />
      <text x="7" y="10" textAnchor="middle" fill="#FFFFFF" fontSize="8" fontWeight="700" fontFamily="'IBM Plex Mono', monospace">A</text>
    </svg>
  ),
  Optimism: (color) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, flexShrink: 0 }}>
      <circle cx="7" cy="7" r="6" fill={color} />
      <text x="7" y="10" textAnchor="middle" fill="#FFFFFF" fontSize="8" fontWeight="700" fontFamily="'IBM Plex Mono', monospace">O</text>
    </svg>
  ),
  Base: (color) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, flexShrink: 0 }}>
      <circle cx="7" cy="7" r="6" fill={color} />
      <text x="7" y="10" textAnchor="middle" fill="#FFFFFF" fontSize="8" fontWeight="700" fontFamily="'IBM Plex Mono', monospace">B</text>
    </svg>
  ),
  Polygon: (color) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, flexShrink: 0 }}>
      <polygon points="7,1 12.5,4 12.5,10 7,13 1.5,10 1.5,4" fill={color} />
      <text x="7" y="10" textAnchor="middle" fill="#FFFFFF" fontSize="7" fontWeight="700" fontFamily="'IBM Plex Mono', monospace">P</text>
    </svg>
  ),
};

// ---------------------------------------------------------------------------
// Skeleton / shimmer card for loading state
// ---------------------------------------------------------------------------

// eslint-disable-next-line no-unused-vars
function SkeletonCard({ colors }) {
  return (
    <div style={{
      background: colors.surface,
      borderRadius: 12,
      padding: 20,
      boxShadow: `0 1px 4px ${colors.shadow}`,
      border: `1px solid ${colors.border}`,
      transition: 'background-color 0.3s ease, border-color 0.3s ease',
    }}>
      {[1, 2, 3, 4].map(i => (
        <div key={i} style={{
          height: i === 1 ? 20 : 14,
          width: i === 1 ? '60%' : i === 4 ? '40%' : '80%',
          background: colors.borderSubtle,
          borderRadius: 6,
          marginBottom: i < 4 ? 12 : 0,
          animation: 'shimmer 1.5s infinite ease-in-out',
          opacity: 0.6,
        }} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Range bar component
// ---------------------------------------------------------------------------

function RangeBar({ tickLower, tickUpper, currentTick, inRange, colors }) {
  // Normalize the current tick's position within the range for display
  const rangeSpan = tickUpper - tickLower;
  const padding = rangeSpan * 0.2;
  const displayMin = tickLower - padding;
  const displayMax = tickUpper + padding;
  const displaySpan = displayMax - displayMin;

  const rangeLeftPct = ((tickLower - displayMin) / displaySpan) * 100;
  const rangeWidthPct = ((tickUpper - tickLower) / displaySpan) * 100;
  const markerPct = Math.max(0, Math.min(100, ((currentTick - displayMin) / displaySpan) * 100));

  const barColor = inRange ? colors.green : colors.red;

  return (
    <div style={{ position: 'relative', height: 12, marginTop: 8, marginBottom: 4 }}>
      {/* Track */}
      <div style={{
        position: 'absolute',
        top: 4,
        left: 0,
        right: 0,
        height: 4,
        background: colors.scoreTrack,
        borderRadius: 2,
      }} />
      {/* Active range */}
      <div style={{
        position: 'absolute',
        top: 4,
        left: `${rangeLeftPct}%`,
        width: `${rangeWidthPct}%`,
        height: 4,
        background: `${barColor}40`,
        borderRadius: 2,
      }} />
      {/* Current price marker */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: `${markerPct}%`,
        width: 3,
        height: 12,
        background: barColor,
        borderRadius: 1.5,
        transform: 'translateX(-50%)',
      }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Position Card
// ---------------------------------------------------------------------------

function PositionCard({ position, historyEntry, colors, isMobile, showChainBadge }) {
  const token0Symbol = position.token0.symbol;
  const token1Symbol = position.token1.symbol;
  const pairName = `${token0Symbol}/${token1Symbol}`;
  const leftBorderColor = position.inRange ? '#0D7A4E' : '#B8001A';
  const chainSlug = CHAIN_SLUG_MAP[position.chain] || 'ethereum';
  const uniswapUrl = `https://app.uniswap.org/positions/v3/${chainSlug}/${position.tokenId}`;
  const posChainColor = CHAIN_COLORS[position.chain] || '#999';

  return (
    <div style={{
      background: colors.surface,
      borderRadius: 12,
      padding: 20,
      boxShadow: `0 1px 4px ${colors.shadow}`,
      border: `1px solid ${colors.border}`,
      borderLeft: `3px solid ${leftBorderColor}`,
      transition: 'background-color 0.3s ease, border-color 0.3s ease',
      position: 'relative',
    }}>
      {/* NFT ID badge — top right */}
      <div style={{
        position: 'absolute',
        top: 12,
        right: 14,
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 9,
        color: '#BBB',
        letterSpacing: 0.5,
      }}>
        NFT #{position.tokenId}
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <TokenStack tokens={[token0Symbol, token1Symbol]} size={28} />
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: 16,
            color: colors.textPrimary,
          }}>
            {pairName}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Fee tier badge */}
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: colors.textSecondary,
              background: colors.badgeBg,
              border: `1px solid ${colors.border}`,
              padding: '1px 6px',
              borderRadius: 3,
              fontWeight: 500,
            }}>
              {fmtFeeTier(position.feeTier)}
            </span>
            {/* In Range badge */}
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              fontWeight: 600,
              color: position.inRange ? colors.green : colors.red,
              background: position.inRange ? colors.greenBg : colors.redBg,
              border: `1px solid ${position.inRange ? colors.greenBorder : colors.redBorder}`,
              padding: '2px 8px',
              borderRadius: 20,
            }}>
              {position.inRange ? 'IN RANGE' : 'OUT OF RANGE'}
            </span>
            {/* Chain badge (shown in All Chains mode) */}
            {showChainBadge && position.chain && (
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                fontWeight: 600,
                color: posChainColor,
                background: `${posChainColor}15`,
                border: `1px solid ${posChainColor}40`,
                padding: '2px 8px',
                borderRadius: 20,
              }}>
                {position.chain}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Range bar */}
      <RangeBar
        tickLower={position.tickLower}
        tickUpper={position.tickUpper}
        currentTick={position.currentTick}
        inRange={position.inRange}
        colors={colors}
      />

      {/* Current Value */}
      <div style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: 22,
        color: colors.textPrimary,
        marginTop: 12,
        marginBottom: 6,
      }}>
        {fmtUsd(position.totalValueUsd)}
      </div>

      {/* Token amounts */}
      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 12,
        color: colors.textSecondary,
        marginBottom: 10,
      }}>
        {fmtTokenAmount(position.amount0)} {token0Symbol} + {fmtTokenAmount(position.amount1)} {token1Symbol}
      </div>

      {/* Unclaimed Fees */}
      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 12,
        color: colors.gold,
        marginBottom: 6,
      }}>
        Fees: {fmtUsd(position.feesUsd)}
      </div>

      {/* Historical data: IL and P&L */}
      {historyEntry && (
        <div style={{
          borderTop: `1px solid ${colors.borderSubtle}`,
          paddingTop: 10,
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}>
          {historyEntry.ilPercent != null && (
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              color: historyEntry.ilPercent < 0 ? colors.red : colors.green,
            }}>
              IL: {fmtPercent(historyEntry.ilPercent)}
            </div>
          )}
          {historyEntry.pnl != null && (
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              color: historyEntry.pnl >= 0 ? colors.green : colors.red,
              fontWeight: 600,
            }}>
              P&L: {historyEntry.pnl >= 0 ? '+' : ''}{fmtUsd(historyEntry.pnl)} ({fmtPercent(historyEntry.pnlPercent)})
            </div>
          )}
          {historyEntry.depositValueUsd != null && (
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: colors.labelMuted,
            }}>
              Deposited: {fmtUsd(historyEntry.depositValueUsd)}
            </div>
          )}
        </div>
      )}

      {/* View on Uniswap link */}
      <div style={{
        borderTop: `1px solid ${colors.borderSubtle}`,
        paddingTop: 10,
        marginTop: historyEntry ? 4 : 8,
      }}>
        <a
          href={uniswapUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: '#888',
            textDecoration: 'none',
          }}
          onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
          onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
        >
          View on Uniswap &#8599;
        </a>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Portfolio Allocation Pie Chart
// ---------------------------------------------------------------------------

function PortfolioAllocationChart({ positions, colors, isMobile }) {
  const data = useMemo(() => {
    const grouped = {};
    for (const pos of positions) {
      const pair = `${pos.token0.symbol}/${pos.token1.symbol}`;
      grouped[pair] = (grouped[pair] || 0) + pos.totalValueUsd;
    }
    return Object.entries(grouped)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [positions]);

  if (data.length < 2) return null;

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.5 }}
      style={{
        background: colors.surface,
        borderRadius: 12,
        padding: isMobile ? 16 : 24,
        boxShadow: `0 1px 4px ${colors.shadow}`,
        border: `1px solid ${colors.border}`,
        marginBottom: 20,
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}
    >
      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10,
        color: colors.labelLight,
        letterSpacing: 1.5,
        fontWeight: 600,
        marginBottom: 16,
      }}>
        PORTFOLIO ALLOCATION
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 16 : 24,
      }}>
        <div style={{ width: 200, height: 200, flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={85}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {data.map((entry, index) => (
                  <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip
                formatter={(value) => fmtUsd(value)}
                contentStyle={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 11,
                  borderRadius: 8,
                  border: `1px solid ${colors.border}`,
                  boxShadow: `0 2px 8px ${colors.shadow}`,
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
          {data.map((entry, index) => (
            <div key={entry.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: PIE_COLORS[index % PIE_COLORS.length],
                flexShrink: 0,
              }} />
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12,
                color: colors.textPrimary,
                flex: 1,
              }}>
                {entry.name}
              </div>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                color: colors.textSecondary,
              }}>
                {total > 0 ? ((entry.value / total) * 100).toFixed(1) : 0}%
              </div>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                color: colors.labelMuted,
              }}>
                {fmtUsd(entry.value)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function WalletAnalyzerView() {
  const { colors } = useTheme();
  const isMobile = useIsMobile();

  const [walletAddress, setWalletAddress] = useState('');
  const [chain, setChain] = useState('Ethereum');
  const [positions, setPositions] = useState([]);
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [error, setError] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);

  const isAllChains = chain === 'All';

  // Enrich positions with historical P&L/IL data
  const enrichedHistory = useMemo(() => {
    if (!historyData || !positions.length) return {};

    const map = {};
    for (const pos of positions) {
      // Try to match by pool tokens + tick range
      const match = historyData.find(h => {
        const hToken0 = h.pool?.token0?.symbol?.toUpperCase();
        const hToken1 = h.pool?.token1?.symbol?.toUpperCase();
        const pToken0 = pos.token0.symbol.toUpperCase();
        const pToken1 = pos.token1.symbol.toUpperCase();
        const tickMatch =
          Number(h.tickLower?.tickIdx) === pos.tickLower &&
          Number(h.tickUpper?.tickIdx) === pos.tickUpper;
        return (
          ((hToken0 === pToken0 && hToken1 === pToken1) ||
            (hToken0 === pToken1 && hToken1 === pToken0)) &&
          tickMatch
        );
      });

      if (match) {
        const dep0 = parseFloat(match.depositedToken0) || 0;
        const dep1 = parseFloat(match.depositedToken1) || 0;
        const collFees0 = parseFloat(match.collectedFeesToken0) || 0;
        const collFees1 = parseFloat(match.collectedFeesToken1) || 0;

        // Use current prices to estimate deposit value (approximate if no historical prices)
        const price0 = pos.amount0Usd / (pos.amount0 || 1);
        const price1 = pos.amount1Usd / (pos.amount1 || 1);

        const depositValueUsd = dep0 * price0 + dep1 * price1;
        const feesCollectedUsd = collFees0 * price0 + collFees1 * price1;

        // Compute IL
        const ilResult = computeIL(
          dep0, dep1,
          pos.amount0, pos.amount1,
          price0, price1,
          price0, price1
        );

        // Compute P&L
        const pnlResult = computePnL(
          pos.totalValueUsd,
          feesCollectedUsd,
          pos.feesUsd,
          depositValueUsd
        );

        map[pos.tokenId] = {
          ilPercent: ilResult.ilPercent,
          pnl: pnlResult.pnl,
          pnlPercent: pnlResult.pnlPercent,
          depositValueUsd,
        };
      }
    }
    return map;
  }, [positions, historyData]);

  // Summary stats
  const summary = useMemo(() => {
    if (positions.length === 0) {
      return { totalValue: 0, unclaimedFees: 0, totalPnl: 0, hasPnl: false, count: 0 };
    }

    const totalValue = positions.reduce((s, p) => s + p.totalValueUsd, 0);
    const unclaimedFees = positions.reduce((s, p) => s + p.feesUsd, 0);

    let totalPnl = 0;
    let hasPnl = false;
    for (const pos of positions) {
      const h = enrichedHistory[pos.tokenId];
      if (h && h.pnl != null) {
        totalPnl += h.pnl;
        hasPnl = true;
      }
    }

    return { totalValue, unclaimedFees, totalPnl, hasPnl, count: positions.length };
  }, [positions, enrichedHistory]);

  // Group positions by chain for "All Chains" display
  const positionsByChain = useMemo(() => {
    if (!isAllChains || positions.length === 0) return null;
    const grouped = {};
    for (const pos of positions) {
      const c = pos.chain || 'Unknown';
      if (!grouped[c]) grouped[c] = [];
      grouped[c].push(pos);
    }
    return grouped;
  }, [positions, isAllChains]);

  const handleAnalyze = useCallback(async () => {
    const trimmed = walletAddress.trim();
    if (!isValidAddress(trimmed)) {
      setError('Invalid address. Must be a 42-character hex address starting with 0x.');
      toast.error('Invalid address. Must be a 42-character hex address starting with 0x.');
      return;
    }

    setLoading(true);
    setError(null);
    setPositions([]);
    setHistoryData(null);
    setLoadingStep('');

    try {
      let allPositions;

      if (isAllChains) {
        // Fetch from all chains in parallel
        setLoadingStep('Fetching positions from all chains...');
        const results = await Promise.allSettled(
          CHAINS.map(c =>
            fetchWalletPositions(trimmed, c, (step) => {
              setLoadingStep(step);
            })
          )
        );

        allPositions = [];
        for (let i = 0; i < results.length; i++) {
          if (results[i].status === 'fulfilled') {
            allPositions.push(...results[i].value);
          }
        }
      } else {
        // Single chain
        allPositions = await fetchWalletPositions(trimmed, chain, (step) => {
          setLoadingStep(step);
        });
      }

      setPositions(allPositions);

      if (allPositions.length === 0) {
        setLoading(false);
        setLoadingStep('');
        return;
      }

      const chainLabel = isAllChains ? 'all chains' : chain;
      toast.success(`Found ${allPositions.length} position${allPositions.length > 1 ? 's' : ''} on ${chainLabel}`);

      // Try to fetch historical data from The Graph (non-blocking)
      // For "All" mode, fetch from each chain that had results
      setLoadingStep('Loading historical data...');
      try {
        if (isAllChains) {
          const chainsWithPositions = [...new Set(allPositions.map(p => p.chain))];
          const historyResults = await Promise.allSettled(
            chainsWithPositions.map(c => fetchPositionHistory(trimmed, c))
          );
          const combinedHistory = [];
          for (const result of historyResults) {
            if (result.status === 'fulfilled' && result.value) {
              combinedHistory.push(...result.value);
            }
          }
          if (combinedHistory.length > 0) {
            setHistoryData(combinedHistory);
          }
        } else {
          const history = await fetchPositionHistory(trimmed, chain);
          if (history) {
            setHistoryData(history);
          }
        }
      } catch (e) {
        console.warn('Historical data unavailable:', e);
      }
    } catch (e) {
      const msg = e.message || 'Failed to fetch positions. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      setLoadingStep('');
    }
  }, [walletAddress, chain, isAllChains]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && walletAddress.trim() && !loading) {
      handleAnalyze();
    }
  }, [walletAddress, loading, handleAnalyze]);

  const handleTryExample = useCallback(() => {
    setWalletAddress(EXAMPLE_WALLET);
    toast('Using example wallet', { icon: '\uD83D\uDC5B' });
  }, []);

  const handleCopyAddress = useCallback(() => {
    const trimmed = walletAddress.trim();
    if (!trimmed) return;
    navigator.clipboard.writeText(trimmed).then(() => {
      toast.success('Address copied!');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  }, [walletAddress]);

  const isAnalyzeEnabled = walletAddress.trim().length > 0 && !loading;
  const hasResults = !loading && !error && positions.length > 0;
  const hasNoPositions = !loading && !error && positions.length === 0 && walletAddress.trim().length > 0;
  const noPositionsChainLabel = isAllChains ? 'any chain' : chain;

  // Summary card data for CountUp
  const summaryCards = useMemo(() => [
    {
      label: 'TOTAL VALUE',
      rawValue: summary.totalValue,
      isUsd: true,
      accent: colors.green,
    },
    {
      label: 'UNCLAIMED FEES',
      rawValue: summary.unclaimedFees,
      isUsd: true,
      accent: colors.gold,
    },
    {
      label: 'TOTAL P&L',
      rawValue: summary.hasPnl ? summary.totalPnl : null,
      isUsd: true,
      showSign: true,
      accent: summary.hasPnl
        ? (summary.totalPnl >= 0 ? colors.green : colors.red)
        : colors.labelMuted,
    },
    {
      label: 'POSITIONS',
      rawValue: summary.count,
      isUsd: false,
      accent: '#5470D4',
    },
  ], [summary, colors]);

  return (
    <div style={{ padding: isMobile ? '12px' : '20px 24px' }}>
      {/* Toast container */}
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            borderRadius: 8,
          },
        }}
      />

      {/* Shimmer keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { opacity: 0.6; }
          50% { opacity: 0.3; }
          100% { opacity: 0.6; }
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: isMobile ? 24 : 28,
          color: colors.textPrimary,
          marginBottom: 6,
        }}>
          Wallet Analyzer
        </div>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 13,
          color: colors.textSecondary,
        }}>
          Analyze your Uniswap V3 positions
        </div>
      </div>

      {/* Input section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        style={{
          background: colors.surface,
          borderRadius: 12,
          padding: isMobile ? 16 : 24,
          boxShadow: `0 1px 4px ${colors.shadow}`,
          border: `1px solid ${colors.border}`,
          marginBottom: 20,
          transition: 'background-color 0.3s ease, border-color 0.3s ease',
        }}
      >
        {/* Wallet address input */}
        <div style={{ marginBottom: 16 }}>
          <label style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            color: colors.labelLight,
            letterSpacing: 1,
            display: 'block',
            marginBottom: 6,
          }}>
            WALLET ADDRESS
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
            <input
              type="text"
              placeholder="Paste wallet address (0x...)"
              value={walletAddress}
              onChange={e => setWalletAddress(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 14,
                padding: '14px 16px',
                borderRadius: 10,
                border: `1.5px solid ${inputFocused ? colors.green : colors.border}`,
                background: colors.inputBg,
                color: colors.textPrimary,
                width: '100%',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s ease, background-color 0.3s ease, color 0.3s ease',
                flex: 1,
              }}
            />
            {/* Copy address button */}
            <button
              onClick={handleCopyAddress}
              disabled={!walletAddress.trim()}
              title="Copy address"
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 14,
                padding: '0 14px',
                borderRadius: 10,
                border: `1.5px solid ${colors.border}`,
                background: colors.inputBg,
                color: walletAddress.trim() ? colors.textSecondary : colors.labelLight,
                cursor: walletAddress.trim() ? 'pointer' : 'not-allowed',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
                <path d="M10.5 5.5V3.5C10.5 2.67 9.83 2 9 2H3.5C2.67 2 2 2.67 2 3.5V9C2 9.83 2.67 10.5 3.5 10.5H5.5" />
              </svg>
            </button>
          </div>
        </div>

        {/* Chain selector */}
        <div style={{ marginBottom: 16 }}>
          <label style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            color: colors.labelLight,
            letterSpacing: 1,
            display: 'block',
            marginBottom: 8,
          }}>
            CHAIN
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* All Chains pill */}
            <button
              onClick={() => setChain('All')}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                padding: '6px 14px',
                borderRadius: 20,
                border: `1.5px solid ${isAllChains ? colors.textPrimary : colors.border}`,
                background: isAllChains ? colors.textPrimary : 'transparent',
                color: isAllChains ? colors.surface : colors.textSecondary,
                cursor: 'pointer',
                fontWeight: 500,
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              All
            </button>
            {CHAINS.map(c => {
              const isSelected = chain === c;
              const chainColor = CHAIN_COLORS[c] || '#999';
              return (
                <button
                  key={c}
                  onClick={() => setChain(c)}
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                    padding: '6px 14px',
                    borderRadius: 20,
                    border: `1.5px solid ${isSelected ? chainColor : colors.border}`,
                    background: isSelected ? chainColor : 'transparent',
                    color: isSelected ? '#FFFFFF' : colors.textSecondary,
                    cursor: 'pointer',
                    fontWeight: 500,
                    transition: 'all 0.15s',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {CHAIN_ICONS[c] && CHAIN_ICONS[c](isSelected ? '#FFFFFF' : chainColor)}
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        {/* Analyze + Try example buttons */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={handleAnalyze}
            disabled={!isAnalyzeEnabled}
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 13,
              fontWeight: 600,
              padding: '14px 32px',
              borderRadius: 8,
              border: 'none',
              background: isAnalyzeEnabled ? colors.green : colors.border,
              color: isAnalyzeEnabled ? '#FFFFFF' : colors.labelLight,
              cursor: isAnalyzeEnabled ? 'pointer' : 'not-allowed',
              transition: 'all 0.15s',
            }}
          >
            {loading ? 'Analyzing...' : 'Analyze'}
          </button>
          <button
            onClick={handleTryExample}
            disabled={loading}
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              padding: '8px 16px',
              borderRadius: 6,
              border: '1px solid #E8EAED',
              background: 'transparent',
              color: '#888',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s',
              opacity: loading ? 0.5 : 1,
            }}
          >
            Try example
          </button>
        </div>
      </motion.div>

      {/* Loading state */}
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ marginBottom: 20 }}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px 0',
            }}>
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: `${colors.green}20`,
                  border: `2px solid ${colors.green}60`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 20,
                }}
              >
                <div style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: colors.green,
                }} />
              </motion.div>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12,
                color: colors.textSecondary,
                textAlign: 'center',
              }}>
                {loadingStep || `Fetching positions from ${isAllChains ? 'all chains' : chain}...`}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error state */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            style={{
              background: colors.redBg,
              borderRadius: 12,
              padding: '16px 20px',
              border: `1px solid ${colors.redBorder}`,
              marginBottom: 20,
            }}
          >
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              color: colors.red,
              fontWeight: 500,
            }}>
              {error}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary cards */}
      {hasResults && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          gap: isMobile ? 10 : 16,
          marginBottom: 20,
        }}>
          {summaryCards.map((card, index) => (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              style={{
                background: colors.surface,
                borderRadius: 12,
                padding: isMobile ? '14px 14px' : '18px 20px',
                boxShadow: `0 1px 4px ${colors.shadow}`,
                border: `1px solid ${colors.border}`,
                borderTop: `3px solid ${card.accent}`,
                transition: 'background-color 0.3s ease, border-color 0.3s ease',
              }}
            >
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                color: colors.labelLight,
                letterSpacing: 1.5,
                fontWeight: 600,
                marginBottom: 8,
              }}>
                {card.label}
              </div>
              <div style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: isMobile ? 20 : 24,
                color: card.accent,
              }}>
                {card.rawValue == null ? '--' : (
                  card.isUsd ? (
                    <CountUp
                      end={card.rawValue}
                      prefix={card.showSign && card.rawValue >= 0 ? '+$' : '$'}
                      decimals={2}
                      duration={1.5}
                      separator=","
                    />
                  ) : (
                    <CountUp
                      end={card.rawValue}
                      duration={1.5}
                      decimals={0}
                    />
                  )
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Portfolio allocation pie chart */}
      {hasResults && (
        <PortfolioAllocationChart positions={positions} colors={colors} isMobile={isMobile} />
      )}

      {/* Position cards */}
      {hasResults && (
        <>
          {isAllChains && positionsByChain ? (
            // Grouped by chain
            Object.entries(positionsByChain).map(([chainName, chainPositions]) => (
              <div key={chainName} style={{ marginBottom: 24 }}>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  color: CHAIN_COLORS[chainName] || colors.labelLight,
                  letterSpacing: 1.5,
                  fontWeight: 600,
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}>
                  {CHAIN_ICONS[chainName] && CHAIN_ICONS[chainName](CHAIN_COLORS[chainName] || colors.labelLight)}
                  {chainName.toUpperCase()} ({chainPositions.length})
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))',
                  gap: 16,
                }}>
                  {chainPositions.map((pos, idx) => (
                    <motion.div
                      key={`${pos.chain}-${pos.tokenId}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: idx * 0.08 }}
                    >
                      <PositionCard
                        position={pos}
                        historyEntry={enrichedHistory[pos.tokenId] || null}
                        colors={colors}
                        isMobile={isMobile}
                        showChainBadge={true}
                      />
                    </motion.div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            // Single chain
            <>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                color: colors.labelLight,
                letterSpacing: 1.5,
                fontWeight: 600,
                marginBottom: 12,
              }}>
                POSITIONS ({positions.length})
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: 16,
                marginBottom: 24,
              }}>
                {positions.map((pos, idx) => (
                  <motion.div
                    key={pos.tokenId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: idx * 0.08 }}
                  >
                    <PositionCard
                      position={pos}
                      historyEntry={enrichedHistory[pos.tokenId] || null}
                      colors={colors}
                      isMobile={isMobile}
                      showChainBadge={false}
                    />
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* No positions found */}
      {hasNoPositions && (
        <div style={{
          textAlign: 'center',
          padding: '60px 0',
        }}>
          <div style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: 24,
            color: colors.labelLighter,
            marginBottom: 8,
          }}>
            No positions found
          </div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            color: colors.labelLight,
          }}>
            This wallet has no active Uniswap V3 positions on {noPositionsChainLabel}.
          </div>
        </div>
      )}

      {/* Empty state — no address entered yet */}
      {!loading && !error && positions.length === 0 && !walletAddress.trim() && (
        <div style={{
          textAlign: 'center',
          padding: isMobile ? '40px 16px' : '80px 0',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>
            {'\uD83D\uDC5B'}
          </div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 14,
            color: colors.textSecondary,
            maxWidth: 460,
            margin: '0 auto',
            lineHeight: 1.6,
            marginBottom: 12,
          }}>
            Paste any Ethereum wallet address to analyze its Uniswap V3 liquidity positions
          </div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            color: colors.labelLight,
          }}>
            No wallet connection needed — all data is public on-chain
          </div>
        </div>
      )}
    </div>
  );
}
